import React, { useEffect, useLayoutEffect, useState } from 'react';
import { debounce } from 'underscore';
import { GAME_WIDTHS } from './constants';
import { useDeviceSelector } from 'js/store/hooks';
import { compose, concat, dropLast, last, length, omit, path, pick, splitEvery } from 'ramda';
import { branch, defaultProps, mapProps, renderNothing, withHandlers, withProps, withState } from 'recompose';

export const GAMES_PER_SLIDE_MIN = 2;

export const getRightMargin = (gameRef) => {
    let marginRight = parseFloat(getComputedStyle(gameRef).marginRight);
    if (marginRight !== 0) {
        return marginRight;
    }

    // for single element in slide possible margin won't be drawn hence we couldn't be able to calculate it
    const el = document.createElement('span');
    gameRef.parentElement.appendChild(el);
    marginRight = parseFloat(getComputedStyle(gameRef).marginRight);
    gameRef.parentElement.removeChild(el);

    return marginRight;
};

export default compose(
    defaultProps({
        fillLastSlide: true,
        gamesPerSlideMin: GAMES_PER_SLIDE_MIN,
        gameWidths: GAME_WIDTHS
    }),
    withState('gamesPerSlide', 'setGamesPerSlide', path(['gamesPerSlideMin'])),
    withProps(() => useDeviceSelector(pick(['isDesktop']))),
    withProps(({ gamesPerSlide, gameCodes, fillLastSlide }) => {
        if (gameCodes.length === 0) {
            return { slides: [] };
        }

        let slides = splitEvery(gamesPerSlide, gameCodes);

        if (fillLastSlide) {
            let lastSlide = last(slides);
            lastSlide = concat(lastSlide, Array(gamesPerSlide - length(lastSlide)).fill(null));

            slides = concat(dropLast(1, slides), [lastSlide]);
        }

        return { slides };
    }),
    branch(
        props => !props.slides.length,
        renderNothing
    ),
    withHandlers(({ setGamesPerSlide, setSlidesLength, isDesktop, gameSize, gamesPerSlideMin, gameWidths }) => {
        let containerRef;
        let gameRef;
        let frame;

        const setSlidesLengthInternal = ({ gamesPerSlide, gameCodes }) =>
            setSlidesLength && setSlidesLength(Math.ceil(gameCodes.length / gamesPerSlide));

        const getGamesPerSlide = ({ gamesPerSlide, gameCodes }) => {
            const formFactor = isDesktop ? 'DESKTOP' : 'MOBILE';
            const minGameWidth = gameWidths[gameSize][formFactor];

            if (containerRef && gameRef) {
                const { floor, max } = Math;
                const { offsetWidth } = containerRef;
                const { paddingLeft, paddingRight } = getComputedStyle(containerRef);
                const gameMarginRight = getRightMargin(gameRef);
                const containerWidth = offsetWidth - parseFloat(paddingLeft) - parseFloat(paddingRight)
                    + gameMarginRight; // last game goes without margin
                const minTotalGameWidth = minGameWidth + gameMarginRight;
                const gamesCount = floor(containerWidth / minTotalGameWidth);
                const totalGamesCount = max(gamesCount, gamesPerSlideMin);

                if (gamesPerSlide !== totalGamesCount) {
                    setGamesPerSlide(totalGamesCount);
                    setSlidesLengthInternal({ gamesPerSlide: totalGamesCount, gameCodes });
                }
            }
        };

        return {
            setSlideRef: props => (ref) => {
                containerRef = ref;
                getGamesPerSlide(props);
            },
            setGameRef: props => (ref) => {
                gameRef = ref;
                getGamesPerSlide(props);
            },
            getGamesPerSlide: props => () => getGamesPerSlide(props),
            throttledGetGamePerSlide: props => () => {
                cancelAnimationFrame(frame);
                frame = requestAnimationFrame(() => getGamesPerSlide(props));
            },
            setSlidesLength: ({ gamesPerSlide, gameCodes }) =>
                () => setSlidesLengthInternal({ gamesPerSlide, gameCodes })
        };
    }),
    Wrapped => (props) => {
        const [slideContainer, setSlideContainer] = useState(null);
        const {
            throttledGetGamePerSlide,
            expanded,
            isVisible,
            getGamesPerSlide,
            setSlidesLength,
            gameCodes,
            setSlideRef
        } = props;
        const { os, browserName } = useDeviceSelector(pick(['os', 'browserName']));
        const debouncedGetGamesPerSlide = debounce(() => {
            os === 'ios' && browserName === 'chrome' && getGamesPerSlide();
        }, 300);

        const handleSetSlideRef = (ref) => {
            setSlideContainer(ref);

            if (typeof setSlideRef === 'function') {
                setSlideRef(ref);
            }
        };

        useEffect(() => {
            const debouncedResizeAndScrollY = debounce(() => {
                throttledGetGamePerSlide();
                window.scrollTo(0, window.scrollY);
            }, 300);
            const resizeObserver = new ResizeObserver(() => {
                debouncedResizeAndScrollY();
            });

            slideContainer && resizeObserver.observe(slideContainer);

            return () => {
                slideContainer && resizeObserver.unobserve(slideContainer);
            };
        }, [slideContainer, throttledGetGamePerSlide]);

        useEffect(() => {
            window.addEventListener('orientationchange', debouncedGetGamesPerSlide);

            return () => {
                window.removeEventListener('orientationchange', debouncedGetGamesPerSlide);
            };
        }, [debouncedGetGamesPerSlide]);

        useLayoutEffect(getGamesPerSlide, [expanded, isVisible, getGamesPerSlide]);
        useLayoutEffect(setSlidesLength, [gameCodes, setSlidesLength]);

        return <Wrapped {...props} setSlideRef={handleSetSlideRef} />;
    },
    mapProps(omit([
        'fillLastSlide',
        'gamesPerSlideMin',
        'isDesktop',
        'gamesPerSlide',
        'setGamesPerSlide',
        'getGamesPerSlide',
        'throttledGetGamePerSlide',
        'setSlidesLength'
    ]))
);
